import { useCallback, useRef } from "react";
import {
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

import { NPM_QUERY_KEYS } from "@app/network-portfolio-management/npmQueryKeys";
import NetworkPortfolioManagementService from "@app/network-portfolio-management/services/NetworkPortfolioManagementService";
import {
  FilterOptions,
  GapByCountyStatusResult,
  GapCountResult,
  GroupByOption,
  NetworkPortfolioManagementResourceView,
  ProjectGroup,
  ProjectResource,
  ProjectResourcesRequest,
  SortByOption,
} from "@app/network-portfolio-management/interfaces";

/**
 * Colocated query-key factories and read/invalidate hooks for the Network Portfolio Management
 * project-list cache. Every `npm-*` key is built here from {@link NPM_QUERY_KEYS}, mirroring the
 * shape of `savedViewsQueryKey` in `utils/savedViewsUtils.ts`, so cache identity lives in exactly
 * one place. Each factory reproduces the exact tuple its call site uses today, so the cache
 * keys are byte-for-byte unchanged — with one exception: `npmFilterOptionsQueryKey` adds
 * `userId` to scope the filter-options cache per user.
 */

type NpmQueryKey = (typeof NPM_QUERY_KEYS)[keyof typeof NPM_QUERY_KEYS];

const useNpmService = (): NetworkPortfolioManagementService =>
  useRef(new NetworkPortfolioManagementService()).current;

// --- Query-key factories ---------------------------------------------------

export const npmProjectIdsQueryKey = (
  userId: number | undefined,
  view: NetworkPortfolioManagementResourceView,
  requestKey: string,
): readonly [typeof NPM_QUERY_KEYS.projectIds, number | undefined, NetworkPortfolioManagementResourceView, string] =>
  [NPM_QUERY_KEYS.projectIds, userId, view, requestKey] as const;

export const npmProjectGroupsQueryKey = (
  userId: number | undefined,
  projectIds: number[],
  groupBy: GroupByOption,
  sortBy: SortByOption,
): readonly [typeof NPM_QUERY_KEYS.projectGroups, number | undefined, number[], GroupByOption, SortByOption] =>
  [NPM_QUERY_KEYS.projectGroups, userId, projectIds, groupBy, sortBy] as const;

export const npmSortedProjectIdsQueryKey = (
  userId: number | undefined,
  projectIds: number[],
  sortBy: SortByOption,
): readonly [typeof NPM_QUERY_KEYS.sortedProjectIds, number | undefined, number[], SortByOption] =>
  [NPM_QUERY_KEYS.sortedProjectIds, userId, projectIds, sortBy] as const;

export const npmGapsWidgetQueryKey = (
  projectIds: number[],
): readonly [typeof NPM_QUERY_KEYS.gapsWidget, number[]] =>
  [NPM_QUERY_KEYS.gapsWidget, projectIds] as const;

// userId is part of the key: the response depends on viewer role/resource visibility, and a bare
// project-id key would let one viewer's cached breakdown leak to another viewer sharing this QueryClient.
export const npmGapByCountyStatusQueryKey = (
  userId: number | undefined,
  projectIds: number[],
): readonly [typeof NPM_QUERY_KEYS.gapByCountyStatus, number | undefined, number[]] =>
  [NPM_QUERY_KEYS.gapByCountyStatus, userId, projectIds] as const;

export const npmFilterOptionsQueryKey = (
  userId: number | undefined,
  view: NetworkPortfolioManagementResourceView,
): readonly [typeof NPM_QUERY_KEYS.filterOptions, number | undefined, NetworkPortfolioManagementResourceView] =>
  [NPM_QUERY_KEYS.filterOptions, userId, view] as const;

// --- Read hooks ------------------------------------------------------------

export function useGetProjectResources(
  userId: number | undefined,
  request: ProjectResourcesRequest,
  view: NetworkPortfolioManagementResourceView,
  requestKey: string,
): UseQueryResult<ProjectResource[], Error> {
  const service = useNpmService();
  return useQuery({
    queryFn: ({ signal }) => service.getProjectResources(request, view, signal),
    queryKey: npmProjectIdsQueryKey(userId, view, requestKey),
  });
}

export function useGetGroupedProjects(
  userId: number | undefined,
  projectIds: number[],
  groupBy: GroupByOption,
  sortBy: SortByOption,
  enabled: boolean,
): UseQueryResult<ProjectGroup[], Error> {
  const service = useNpmService();
  return useQuery({
    enabled,
    queryFn: ({ signal }) => service.getGroupedProjectResources(projectIds, groupBy, sortBy, signal),
    queryKey: npmProjectGroupsQueryKey(userId, projectIds, groupBy, sortBy),
  });
}

export function useGetSortedProjectIds(
  userId: number | undefined,
  projectIds: number[],
  sortBy: SortByOption,
  enabled: boolean,
): UseQueryResult<number[], Error> {
  const service = useNpmService();
  return useQuery({
    enabled,
    queryFn: ({ signal }) => service.getSortedProjectIds(projectIds, sortBy, signal),
    queryKey: npmSortedProjectIdsQueryKey(userId, projectIds, sortBy),
  });
}

export function useGetGapsCount(
  projectIds: number[] | undefined,
): UseQueryResult<GapCountResult, Error> {
  const service = useNpmService();
  return useQuery({
    enabled: !!projectIds,
    queryFn: ({ signal }) => service.getGapsCount(projectIds!, signal),
    queryKey: npmGapsWidgetQueryKey(projectIds!),
  });
}

export function useGetFilterOptions(
  userId: number | undefined,
  view: NetworkPortfolioManagementResourceView,
): UseQueryResult<FilterOptions, Error> {
  const service = useNpmService();
  return useQuery({
    // Filter options are scoped to the user's accessible-projects set, which doesn't frequently change.
    // Treating the result as fresh for 5 minutes avoids re-fetching on every remount.
    gcTime: 10 * 60 * 1000,
    queryFn: ({ signal }) => service.getFilterOptions(view, signal),
    queryKey: npmFilterOptionsQueryKey(userId, view),
    staleTime: 5 * 60 * 1000,
  });
}

// One query per already-fetched page rather than per-card/per-row or across the whole accumulated
// list - see useGapByCountyStatusByPage (the caller) for why pages are the batching unit.
export function useGetGapByCountyStatusQueries(
  userId: number | undefined,
  pageProjectIds: number[][],
): UseQueryResult<GapByCountyStatusResult[], Error>[] {
  const service = useNpmService();
  return useQueries({
    queries: pageProjectIds.map((ids) => ({
      enabled: ids.length > 0,
      queryFn: ({ signal }: { signal: AbortSignal }) => service.getGapByCountyStatus(ids, signal),
      queryKey: npmGapByCountyStatusQueryKey(userId, ids),
    })),
  });
}

// --- Invalidation hooks ----------------------------------------------------

// Invalidation stays prefix-only (matching the pre-centralization semantics): every cache entry
// whose key starts with the given prefix is invalidated, regardless of its remaining tuple members.
const useInvalidateKey = (key: NpmQueryKey): (() => Promise<void>) => {
  const queryClient = useQueryClient();
  return useCallback(
    async () => { await queryClient.invalidateQueries({ queryKey: [key] }); },
    [queryClient, key],
  );
};

export const useInvalidateProjectIds = (): (() => Promise<void>) =>
  useInvalidateKey(NPM_QUERY_KEYS.projectIds);

export const useInvalidateProjects = (): (() => Promise<void>) =>
  useInvalidateKey(NPM_QUERY_KEYS.projects);

export const useInvalidateProjectGroups = (): (() => Promise<void>) =>
  useInvalidateKey(NPM_QUERY_KEYS.projectGroups);

export const useInvalidateSortedProjectIds = (): (() => Promise<void>) =>
  useInvalidateKey(NPM_QUERY_KEYS.sortedProjectIds);

export const useInvalidateGapsCount = (): (() => Promise<void>) =>
  useInvalidateKey(NPM_QUERY_KEYS.gapsWidget);

export const useInvalidateFilterOptions = (): (() => Promise<void>) =>
  useInvalidateKey(NPM_QUERY_KEYS.filterOptions);

export const useInvalidateGapByCountyStatus = (): (() => Promise<void>) =>
  useInvalidateKey(NPM_QUERY_KEYS.gapByCountyStatus);

/**
 * Invalidates the project-list-identity keys in one call: the filtered ID set (`npm-project-ids`),
 * the paged project data (`npm-projects`), and the grouped/sorted views (`npm-project-groups`,
 * `npm-sorted-project-ids`). Any project action (single or bulk) that changes membership of the list
 * calls this so every view refreshes together — the grouped and sorted views included, so no view is
 * left stale until a full reload.
 *
 * Also invalidates `npm-gap-by-county-status`: unlike `npm-gaps-widget`/`npm-filter-options` (genuinely
 * unaffected by any project-list action, so deliberately excluded below), publishing or updating a
 * project's options can change its gap-by-county-status breakdown, so it belongs in this same set —
 * one place to update so every call site (single-project actions, bulk actions, bulk archive) stays
 * in sync rather than each one hand-rolling its own extra invalidation.
 */
export function useNpmInvalidateQueries(): () => Promise<void> {
  const invalidateProjectIds = useInvalidateProjectIds();
  const invalidateProjects = useInvalidateProjects();
  const invalidateProjectGroups = useInvalidateProjectGroups();
  const invalidateSortedProjectIds = useInvalidateSortedProjectIds();
  const invalidateGapByCountyStatus = useInvalidateGapByCountyStatus();

  return useCallback(
    async () => {
      await Promise.all([
        invalidateProjectIds(),
        invalidateProjects(),
        invalidateProjectGroups(),
        invalidateSortedProjectIds(),
        invalidateGapByCountyStatus(),
      ]);
    },
    [invalidateProjectIds, invalidateProjects, invalidateProjectGroups, invalidateSortedProjectIds, invalidateGapByCountyStatus],
  );
}
